/**
 * Floating AHJ / setback notes panel. Portaled to `.app` so iPad iframe
 * overflow and stacking contexts cannot swallow the toggle.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { Button, Divider, Tabs } from 'antd';
import { useViewerStore } from "../../../../store/store";

export const SETBACK_NOTES_OPENED_EVENT = 'onedraw:setback-notes-opened';

interface RoofNotesProps {
    notes: string[];
    className?: string;
}

interface NoteProps {
    icon: React.ReactNode,
    title: String,
}

const RoofNotes: React.FC<RoofNotesProps> = ({ notes, className }) => (
    <>
        {notes?.map((item, index) => (
            <ul key={index}>
                <li>
                    <p className={`note__content ${className ?? ""}`}>
                        {index + 1} - {item}
                    </p>
                </li>
                <br />
            </ul>
        ))}
    </>
);

const Note: React.FC<NoteProps> = ({ icon, title }) => {

    const [expand, setExpand] = useState(true);
    const [portalTarget, setPortalTarget] = useState<Element | null>(null);

    const aHJ_Township = useViewerStore(state => state.aHJ_Township);
    const setbackNotes = useViewerStore(state => state.setbackNotes);
    const groundMountNotes = useViewerStore(state => state.groundMountNotes);

    const handleExpand = () => setExpand(!expand);

    const handleTogglePointerDown = (event: React.PointerEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    useEffect(() => {
        setPortalTarget(document.querySelector('.app') ?? document.body);
    }, []);

    useEffect(() => {
        const handleMedia = () => {
            if (window.innerWidth < 1025) {
                setExpand(false);
            } else {
                setExpand(true);
            }
        };
        handleMedia();
        window.addEventListener('resize', handleMedia);
        return () => window.removeEventListener('resize', handleMedia);
    }, []);

    useEffect(() => {
        if (expand) {
            window.dispatchEvent(new CustomEvent(SETBACK_NOTES_OPENED_EVENT));
        }
    }, [expand]);

    const items = [
        {
            key: '1',
            label: 'Roof',
            children: <RoofNotes notes={setbackNotes} />,
        },
        {
            key: '2',
            label: 'Ground Mount',
            children: <RoofNotes notes={groundMountNotes} className="ground-mount-note" />,
        }
    ];

    const noteNode = (
        <div className={`note note__area ${expand ? 'expand' : 'collapse'}`}>
            <div className='note__wrapper'>
                <div className='note__header'>
                    <div className='note__header--column'>
                        {icon}
                        <span className='note__title'>{title}</span>
                    </div>
                    <div className='note__header--column gap-1'>
                        <Button type='text' size='small' className='note__expand' onClick={handleExpand}>
                            <MdClose />
                        </Button>
                    </div>
                </div>
                <Divider />
                <div className='note__title'>AHJ Township : {aHJ_Township}</div>
                <br/>
                <Tabs type={"card"} defaultActiveKey="1" items={items} className="custom-tabs" />
            </div>
            <Button
                title={"Setback Notes"}
                type='default'
                icon={icon}
                className={`note__toggle ${expand && 'show'}`}
                style={{ pointerEvents: 'auto' }}
                onClick={handleExpand}
                onPointerDown={handleTogglePointerDown}
            />
        </div>
    );

    if (!portalTarget) {
        return noteNode;
    }

    return createPortal(noteNode, portalTarget);
};

export default Note;
